import { useEffect, useState, type ComponentType } from 'react'
import { Link, Navigate } from '@tanstack/react-router'
import { ArrowUpRight, BadgeCheck, Sparkles } from 'lucide-react'
import { useLang } from '@/i18n/useLang'
import { useLocalized } from '@/i18n/useLocalized'
import { useSession, useSessionProbe } from '@/features/auth/session'
import { useHomeRoute } from '@/features/auth/home-route'
import { ToolsSection } from '../components/ToolsSection'
import { JoinExplorer } from '../components/JoinExplorer'

/**
 * Витрина схем приезжает ОТДЕЛЬНЫМ куском и только в браузере.
 *
 * Два ограничения сходятся в одном приёме. Лэндингу запрещено импортировать
 * моделлер (`CLAUDE.md`) — а витрина берёт оттуда фигуры, цвета и цену, — и
 * маркетинговые страницы собираются в статику, где `React.lazy` отдал бы в HTML
 * заглушку. Динамический импорт в эффекте решает оба: в статике на этом месте
 * стоит настоящий текст секции, критический путь лэндинга не тяжелеет ни на
 * байт, а браузер подгружает проигрыватель после первой отрисовки.
 */
interface ShowcaseModule {
  SchemeFilm: ComponentType
  SchemeAlphabet: ComponentType
  FindPro: ComponentType
  SchemeSketch: ComponentType
  VocabularyRow: ComponentType

}

function useShowcase(): ShowcaseModule | null {
  const [module, setModule] = useState<ShowcaseModule | null>(null)
  useEffect(() => {
    let alive = true
    void import('@/features/showcase/landing-blocks').then((loaded) => {
      if (alive) setModule(loaded)
    })
    return () => {
      alive = false
    }
  }, [])
  return module
}

export function HomePage() {
  const { t, lang } = useLang()
  const L = useLocalized()
  const showcase = useShowcase()
  // Somebody with a session did not come here to read the pitch. Only the
  // ROOT redirects: /courses, /books and the catalog are pages a signed-in
  // person may legitimately want, and turning the whole public site into a
  // trapdoor would be a different, worse product.
  //
  // 'loading' renders the page as usual — the probe answers in milliseconds,
  // and blanking the marketing home while it does would cost every anonymous
  // visitor a flash of nothing to spare the signed-in one a flash of the site.
  const signedIn = useSessionProbe() === 'authenticated'
  const account = useSession((session) => session.user)
  // Куда вести — по роли и активности (auth/home-route.ts). Пока решения нет,
  // страница рисуется как обычно — по той же причине, что и во время пробы.
  const home = useHomeRoute(signedIn ? account : null)
  const [vol1, vol2] = t.books.bookList

  useEffect(() => {
    const statEls = document.querySelectorAll<HTMLElement>('.stat-value')
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    const runCounter = (el: HTMLElement) => {
      if (el.dataset.counted === '1') return
      el.dataset.counted = '1'
      const target = Number(el.dataset.target ?? 0)
      if (reduced || target === 0) {
        el.textContent = target.toLocaleString()
        return
      }
      el.textContent = '0'
      const duration = 1500
      const start = performance.now()
      const step = (now: number) => {
        const p = Math.min((now - start) / duration, 1)
        const eased = p === 1 ? 1 : 1 - Math.pow(2, -10 * p)
        el.textContent = Math.floor(target * eased).toLocaleString()
        if (p < 1) requestAnimationFrame(step)
        else el.textContent = target.toLocaleString()
      }
      requestAnimationFrame(step)
    }

    const counter = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        const el = entry.target as HTMLElement
        runCounter(el)
        counter.unobserve(el)
      })
    }, { threshold: 0.15 })

    statEls.forEach((el) => {
      const rect = el.getBoundingClientRect()
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        runCounter(el)
      } else {
        counter.observe(el)
      }
    })

    return () => {
      counter.disconnect()
    }
  }, [])

  if (signedIn && home) {
    // replace: the landing page must not sit in history behind the cabinet, or
    // Back from the cabinet lands here and bounces straight forward again.
    return <Navigate to={home} replace />
  }

  return (
    <div>
      <section className="home-intro">
        <div className="container">
          <div className="home-intro-copy">
            <h1 className="rise">{t.hero.h1a}<span>{t.hero.h1accent}</span>{t.hero.h1b}</h1>
            <div className="home-intro-details">
              <p className="home-intro-lead rise rise-1">{t.hero.lead}</p>
              <div className="hero-actions rise rise-2">
                <Link to="/psymodeler" className="btn btn-primary btn-lg">{t.hero.ctaDraw}<ArrowUpRight size={18} /></Link>
                <Link to={L('/find-psychologist')} className="btn btn-outline btn-lg">{t.hero.ctaFind}</Link>
              </div>
              <div className="hero-trust rise rise-3"><span>{t.hero.trust}</span><Link to="/register">{lang === 'ru' ? 'Создать аккаунт' : 'Create an account'} →</Link></div>
            </div>
          </div>
          <div className="home-book-stage">
            <div className="home-book-meta"><span>{t.hero.badge}</span><span>{t.books.editionBadge}</span></div>
            <div className="home-book-grid">
              <div className="book-duo home-book-duo">
                <span className="book-display-word" aria-hidden="true">{lang === 'ru' ? 'АЧП' : 'AHM'}</span>
                <span className="book book--back"><img src={vol1.cover} alt={`${vol1.title}. ${vol1.subtitle}`} width={700} height={990} /></span>
                <span className="book book--front"><img src={vol2.cover} alt={`${vol2.title}. ${vol2.subtitle}`} width={700} height={990} /></span>
                <span className="book-duo-badge badge-new"><Sparkles size={12} aria-hidden="true" />{t.books.newBadge}</span>
              </div>
              <div className="home-book-copy">
                <h2>{vol1.title}</h2>
                <p>{lang === 'en' ? 'Two volumes, revised 2026 editions' : 'Два тома, новое издание 2026'}</p>
                <span className="home-book-pages">{vol1.pages} + {vol2.pages} {t.books.pagesLabel}</span>
                <div className="price-row"><span className="price-main">{vol1.priceDigital}</span><span className="price-side">{lang === 'en' ? 'digital · print available' : 'электронная · есть печатная'}</span></div>
                <a href={vol2.url} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-lg">{t.books.buyVol2Cta}<ArrowUpRight size={18} aria-hidden="true" /></a>
                <Link to={L('/books')} className="home-book-more">{lang === 'ru' ? 'Открыть мир книг АЧП' : 'Explore the AHM books'} →</Link>
              </div>
            </div>
          </div>
          <nav className="home-explore" aria-label={lang === 'ru' ? 'Открывайте АЧП' : 'Explore AHM'}>
            {[[t.nav.books, '/books'], [t.nav.courses, '/courses'], [lang === 'ru' ? 'Практика' : 'Practice', '/practice'], [t.nav.app, '/app']].map(([label, href]) => <Link key={href} to={L(href)}>{label}<ArrowUpRight size={16} /></Link>)}
          </nav>
        </div>
      </section>

      {/* Живая схема: короткий путь от «что такое АЧП» до «это же про меня» */}
      <section id="demo" className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <div data-animate style={{ marginBottom: 'var(--space-6)', maxWidth: '62ch' }}>
            <h2 className="t-headline" style={{ marginBottom: 'var(--space-3)' }}>{t.demo.title}</h2>
            <p style={{ color: 'var(--color-text-muted)', lineHeight: 1.65 }}>{t.demo.lead}</p>
          </div>

          <div data-animate>
            {showcase
              ? <showcase.SchemeFilm />
              : (
                  // Статике и первому кадру достаётся не пустота, а то же
                  // обещание словами: секция читается и без JavaScript.
                  <div className="demo-skeleton">{t.demo.hint}</div>
                )}
          </div>

          <div
            data-animate
            style={{
              display: 'flex',
              gap: 'var(--space-3)',
              flexWrap: 'wrap',
              alignItems: 'center',
              marginTop: 'var(--space-6)',
            }}
          >
            {/* У моделлера нет русского адреса — язык там живёт в настройке
                браузера, а не в пути, поэтому ссылка без префикса. */}
            <Link to="/psymodeler" className="btn btn-primary btn-lg">{t.demo.ctaModeler}</Link>
            <Link to="/register" className="btn btn-outline btn-lg">{t.demo.ctaRegister}</Link>
            <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)', margin: 0, maxWidth: '44ch' }}>
              {t.demo.note}
            </p>
          </div>
        </div>
      </section>

      {/* Мостик к словарю: тринадцать строк с ценами задерживали человека ровно
          там, где ему пора нажимать «нарисовать», поэтому справочник уехал на
          свою страницу, а здесь остались фигуры и одна строка про них. */}
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <div
            data-animate
            className="abc-teaser"
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr auto',
              gap: 'clamp(var(--space-4), 3vw, var(--space-8))',
              alignItems: 'center',
              paddingBlock: 'var(--space-6)',
              borderTop: '1px solid var(--color-divider)',
              borderBottom: '1px solid var(--color-divider)',
            }}
          >
            {showcase ? <showcase.VocabularyRow /> : <span />}
            <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)', lineHeight: 1.6, margin: 0, maxWidth: '46ch' }}>
              {t.alphabet.teaser}
            </p>
            {/* ДВЕ двери справочника, и на главной они стоят рядом не ради
                симметрии: словарь схемы объясняет РИСУНОК, классификация —
                саму систему. Ссылка на классификацию с главной нужна ещё и
                затем, чтобы она лежала в одном клике от корня: у поисковика и
                у модели глубина обхода — это и есть вес страницы. */}
            <div style={{ display: 'grid', gap: 6, justifyItems: 'end' }}>
              <Link to={L('/alphabet')} className="link-blue" style={{ fontSize: 'var(--text-sm)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                {t.alphabet.teaserCta} →
              </Link>
              <Link to={L('/classification')} className="link-blue" style={{ fontSize: 'var(--text-sm)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                {t.nav.classification} →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Каталог: следующий шаг для того, кто узнал свою схему */}
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <div
            data-animate
            className="pro-card"
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 'clamp(var(--space-6), 4vw, var(--space-12))',
              padding: 'clamp(var(--space-6), 4vw, var(--space-10))',
              borderRadius: 'var(--radius-xl)',
              border: '1px solid oklch(from var(--color-accent) l c h / 0.4)',
              background: 'linear-gradient(140deg, oklch(from var(--color-accent) l c h / 0.12) 0%, transparent 60%), var(--color-surface)',
            }}
          >
            <div>
              <h2 className="t-headline" style={{ marginBottom: 'var(--space-3)', maxWidth: '18ch' }}>
                {t.findPro.title}
              </h2>
              <p style={{ color: 'var(--color-text-muted)', lineHeight: 1.65, marginBottom: 'var(--space-6)', maxWidth: '52ch' }}>
                {t.findPro.lead}
              </p>
              <ul style={{ display: 'grid', gap: 'var(--space-4)', listStyle: 'none', margin: '0 0 var(--space-6)', padding: 0 }}>
                {t.findPro.points.map((point) => (
                  <li key={point.title} style={{ display: 'grid', gridTemplateColumns: '22px 1fr', gap: 'var(--space-3)' }}>
                    <BadgeCheck className="find-pro-check" size={22} aria-hidden="true" style={{ marginTop: 2 }} />
                    <div>
                      <p style={{ fontWeight: 650, marginBottom: 2, fontSize: 'var(--text-sm)' }}>{point.title}</p>
                      <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)', lineHeight: 1.55, margin: 0 }}>
                        {point.body}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
              <Link to={L('/certification')} className="link-blue" style={{ fontSize: 'var(--text-sm)' }}>
                {t.findPro.secondary} →
              </Link>
            </div>

            {/* Поиск, а не кнопка «открыть каталог»: обещание про каталог
                проверяется одним действием, и делать это лучше здесь, пока
                читатель ещё на странице. */}
            <div>
              {showcase
                ? <showcase.FindPro />
                : (
                    <Link to={L('/find-psychologist')} className="btn btn-primary btn-lg">
                      {t.findPro.cta}
                    </Link>
                  )}
            </div>
          </div>
        </div>
      </section>

      {/* The psychologist sees how the directory works just above, then gets
          a direct invitation to establish a practice of their own. */}
      <section id="become-pro" className="section" aria-labelledby="practice-title" style={{ paddingTop: 0 }}>
        <div className="container">
          <div className="pro-join">
            <div className="pro-join-head" data-animate>
              <h2 className="t-headline pro-join-title" id="practice-title">
                {t.becomePro.title.split(' — ')[0]} — <span>{t.becomePro.title.split(' — ')[1]}</span>
              </h2>
              <div className="pro-join-intro-copy">
                <p className="pro-join-lead">{t.becomePro.lead}</p>
                <div className="pro-join-actions">
                  <Link to={L('/practice')} className="practice-page-link">{lang === 'ru' ? 'Всё для вашей практики' : 'Explore your practice'} <ArrowUpRight size={16} /></Link>
                  {/* `next` ведёт не в кабинет вообще, а в анкету практики: путь
                      от «хочу» до заполненного профиля не должен проходить через
                      поиск нужного раздела. */}
                  <Link
                    to="/register"
                    search={{ next: '/cabinet/practice/profile' }}
                    className="btn btn-primary btn-lg"
                  >
                    {t.becomePro.cta}
                  </Link>
                  <Link to={L('/certification')} className="link-blue" style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>
                    {t.becomePro.secondary} →
                  </Link>
                </div>
              </div>
            </div>

            <JoinExplorer text={t.becomePro} />
          </div>
        </div>
      </section>

      {/* Две двери, а не две одинаковые карточки с текстом: слева работающая
          поверхность — набросок, который можно собрать не уходя со страницы,
          справа то, что делают дальше. Карточка «иконка + заголовок + абзац»
          на этом месте ничего не предлагала сделать. */}
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <div className="try-row" style={{ display: 'grid', gridTemplateColumns: '1.35fr 1fr', gap: 'clamp(var(--space-8), 5vw, var(--space-14, var(--space-12)))', alignItems: 'start' }}>
            <div data-animate>
              <h2 className="t-headline" style={{ marginBottom: 'var(--space-3)', maxWidth: '20ch' }}>
                {t.try.title}
              </h2>
              <p style={{ color: 'var(--color-text-muted)', lineHeight: 1.65, marginBottom: 'var(--space-6)', maxWidth: '54ch' }}>
                {t.try.lead}
              </p>
              {showcase
                ? <showcase.SchemeSketch />
                : (
                    <Link to="/psymodeler" className="btn btn-primary btn-lg">{t.try.cta}</Link>
                  )}
            </div>

            <div data-animate style={{ display: 'grid', gap: 'var(--space-6)' }}>
              {t.try.next.map((step, i) => (
                <div
                  key={step.title}
                  style={{
                    display: 'grid',
                    gap: 6,
                    paddingTop: i === 0 ? 0 : 'var(--space-6)',
                    borderTop: i === 0 ? 'none' : '1px solid var(--color-divider)',
                  }}
                >
                  <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 700, letterSpacing: '-0.02em' }}>
                    {step.title}
                  </h3>
                  <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)', lineHeight: 1.6, margin: 0, maxWidth: '38ch' }}>
                    {step.body}
                  </p>
                  <Link to={L(step.href as '/find-psychologist')} className="link-blue" style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>
                    {step.cta} →
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* News */}
      <section id="news" className="section" style={{ paddingBlock: 'clamp(var(--space-12), 6vw, var(--space-20))' }}>
        <div className="container">
          <div data-animate>
            <h2 className="t-subhead" style={{ color: 'var(--color-text)', marginBottom: 'var(--space-4)', fontSize: 'var(--text-lg)' }}>{t.news.label}</h2>
            <div
              className="news-card"
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr minmax(120px, 170px)',
                gap: 'clamp(var(--space-6), 4vw, var(--space-12))',
                alignItems: 'center',
                padding: 'clamp(var(--space-6), 4vw, var(--space-10))',
                borderRadius: 'var(--radius-xl)',
                border: '1px solid oklch(from var(--color-accent) l c h / 0.45)',
                background: 'linear-gradient(120deg, oklch(from var(--color-accent) l c h / 0.1) 0%, transparent 55%), var(--color-surface)',
                boxShadow: '0 0 0 4px oklch(from var(--color-accent) l c h / 0.06), var(--shadow-md)',
              }}
            >
              <div>
                <p className="t-mono" style={{ fontSize: '0.74rem', color: 'var(--color-accent)', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 'var(--space-3)' }}>
                  {t.news.date}
                </p>
                <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.12, marginBottom: 'var(--space-3)', maxWidth: '24ch' }}>
                  {t.news.title}
                </h2>
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', lineHeight: 1.65, maxWidth: '58ch', marginBottom: 'var(--space-6)' }}>
                  {t.news.text}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
                  <a href={vol2.url} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-md">
                    {t.news.cta}
                    <ArrowUpRight size={16} aria-hidden="true" />
                  </a>
                  <Link to={L("/books")} className="link-blue" style={{ fontSize: 'var(--text-sm)' }}>
                    {t.news.secondary} →
                  </Link>
                </div>
              </div>
              <a href={vol2.url} target="_blank" rel="noopener noreferrer" className="book news-cover" style={{ transform: 'rotate(4deg)' }} aria-label={`${vol2.title} · Ridero`}>
                <img src={vol2.cover} alt={`${vol2.title}. ${vol2.subtitle}`} loading="lazy" width={700} height={990} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section>
        <div className="container">
          <div className="stats-grid" data-animate-stagger style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-8)', paddingBlock: 'var(--space-16)', borderTop: '1px solid var(--color-divider)', borderBottom: '1px solid var(--color-divider)' }}>
            {[
              { n: 6843, suffix: '+', label: t.stats.followers },
              { n: 117, suffix: '+', label: t.stats.countriesReached },
              { n: 2387419, suffix: '+', label: t.stats.insights },
            ].map((s) => (
              <div key={s.label}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                  <span className="stat-value t-mono" data-target={s.n} style={{ fontSize: 'var(--text-2xl)', fontWeight: 750, letterSpacing: '-0.03em', lineHeight: 1 }}>{s.n.toLocaleString()}</span>
                  <span style={{ fontSize: 'var(--text-lg)', color: 'var(--color-accent)' }}>{s.suffix}</span>
                </div>
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', marginTop: 'var(--space-2)' }}>{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ToolsSection />

      {/* Final CTA */}
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container card final-cta" data-animate style={{ padding: 'clamp(var(--space-8), 5vw, var(--space-12))' }}>
          <h2 className="t-headline" style={{ marginBottom: 'var(--space-3)' }}>{t.finalCta.title}</h2>
          <p style={{ color: 'var(--color-text-muted)', marginBottom: 'var(--space-6)', maxWidth: '56ch', lineHeight: 1.65 }}>
            {t.finalCta.body}
          </p>
          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
            <Link to="/psymodeler" className="btn btn-primary btn-lg">{t.hero.ctaDraw}</Link>
            <Link to={L("/find-psychologist")} className="btn btn-ghost btn-lg">{t.hero.ctaFind}</Link>
          </div>
        </div>
      </section>

      {/* Комментарии — здесь, а не внутри `<style>`: содержимое тега уезжает
          в HTML обеих локалей, и объяснение для читателя кода незачем грузить
          читателю страницы. `.demo-skeleton` — место витрины до того, как
          приедет её чанк: в статике и в первом кадре здесь стоит обещание
          словами, а не прыгающая пустота. */}
      <style>{`
        .demo-skeleton {
          display: grid;
          place-items: center;
          min-height: clamp(320px, 34vw, 440px);
          padding: var(--space-6);
          border: 1px dashed var(--color-border);
          border-radius: var(--radius-xl);
          background: var(--color-surface);
          color: var(--color-text-muted);
          font-size: var(--text-sm);
          text-align: center;
        }
        @media (max-width: 900px) {
          .pro-card,
          .try-row {
            grid-template-columns: 1fr !important;
          }
        }
        @media (max-width: 1023px) {
          .feature-step {
            grid-template-columns: 1fr !important;
            gap: var(--space-4) !important;
          }
        }
        @media (max-width: 900px) {
          .home-hero {
            grid-template-columns: 1fr !important;
          }
          .news-card {
            grid-template-columns: 1fr !important;
          }
          .news-cover {
            max-width: 150px;
            justify-self: start;
          }
        }
        @media (max-width: 767px) {
          .bento-row {
            display: grid;
            grid-template-columns: 1fr !important;
          }
          .bento-row .card {
            grid-column: span 1 !important;
          }
        }
      `}</style>
    </div>
  )
}
